import apiClient from '../api/apiClient';
import { fieldDb, FieldEntityType, FieldOutboxAction } from './fieldDb';

export const FIELD_CACHE_SCHEMA_VERSION = 'worker-dispatch-selected-workers-v1';
const FIELD_CACHE_SCHEMA_VERSION_KEY = 'fieldCacheSchemaVersion';
const MAX_OUTBOX_ATTEMPTS = 25;

interface SyncPullResponse {
  serverTime: string;
  cursor: string;
  data: Record<string, Array<Record<string, any>>>;
}

interface SyncPushResult {
  clientUuid: string;
  entityType: string;
  status: 'applied' | 'duplicate' | 'conflict' | 'rejected';
  serverId?: string;
  conflictId?: string;
  message?: string;
}

interface SyncPushResponse {
  results: SyncPushResult[];
}

interface FieldEvidencePhotoUploadResponse {
  fieldEvidenceId: string;
  documentId: string;
  fileUrl: string;
  storageKey: string;
}

const responseLevels = (response: unknown): unknown[] => [
  response,
  (response as { data?: unknown })?.data,
  ((response as { data?: { data?: unknown } })?.data)?.data,
].filter(Boolean);

const resolveSyncPullResponse = (response: unknown): SyncPullResponse => {
  const level = responseLevels(response).find((candidate) => {
    const row = candidate as Record<string, unknown>;
    return typeof row === 'object' &&
      row !== null &&
      ('serverTime' in row || 'cursor' in row) &&
      'data' in row;
  });

  return (level ?? response) as SyncPullResponse;
};

const resolveSyncPushResponse = (response: unknown): SyncPushResponse => {
  const level = responseLevels(response).find((candidate) => {
    const row = candidate as Record<string, unknown>;
    return typeof row === 'object' &&
      row !== null &&
      Array.isArray(row.results);
  });

  return (level ?? response) as SyncPushResponse;
};

const resolveFieldEvidencePhotoUploadResponse = (response: unknown): FieldEvidencePhotoUploadResponse => {
  const level = responseLevels(response).find((candidate) => {
    const row = candidate as Record<string, unknown>;
    return typeof row === 'object' &&
      row !== null &&
      typeof row.fieldEvidenceId === 'string';
  });

  return (level ?? response) as FieldEvidencePhotoUploadResponse;
};

const dataUrlToBlob = (dataUrl: string): Blob => {
  const match = dataUrl.match(/^data:([^;,]+)?(;base64)?,(.*)$/);
  if (!match) {
    throw new Error('Photo payload is not a valid data URL');
  }

  const mimeType = match[1] || 'application/octet-stream';
  const isBase64 = Boolean(match[2]);
  const payload = match[3] || '';
  const binary = isBase64 ? atob(payload) : decodeURIComponent(payload);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
};

const entityMap: Record<string, FieldEntityType> = {
  tasks: 'task',
  qcTemplates: 'qc_template',
  qcRuns: 'qc_run',
  openDefects: 'defect',
  dailyFieldReports: 'daily_field_report',
  timesheets: 'timesheet',
  fieldEvidence: 'field_evidence',
  units: 'unit',
  components: 'component',
  teams: 'team',
  workers: 'worker',
};

class FieldSyncService {
  async ensureFieldCacheSchemaVersion(): Promise<void> {
    const current = await fieldDb.syncMetadata.get(FIELD_CACHE_SCHEMA_VERSION_KEY);
    if (current?.value === FIELD_CACHE_SCHEMA_VERSION) return;

    const updatedAt = new Date().toISOString();
    await fieldDb.transaction('rw', fieldDb.fieldCache, fieldDb.syncMetadata, async () => {
      await fieldDb.fieldCache.clear();
      await fieldDb.syncMetadata.delete('pullCursor');
      await fieldDb.syncMetadata.put({
        key: FIELD_CACHE_SCHEMA_VERSION_KEY,
        value: FIELD_CACHE_SCHEMA_VERSION,
        updatedAt,
      });
    });
  }

  async flushPending(): Promise<{ synced: number; conflicts: number; failed: number }> {
    const candidates = await fieldDb.fieldOutbox
      .where('status')
      .anyOf(['pending', 'failed'])
      .sortBy('createdAt');
    const actions = candidates.filter((action) => action.attemptCount < MAX_OUTBOX_ATTEMPTS);

    if (actions.length === 0) {
      return { synced: 0, conflicts: 0, failed: 0 };
    }

    const now = new Date().toISOString();
    await fieldDb.fieldOutbox.bulkPut(actions.map((action) => ({
      ...action,
      status: 'syncing',
      attemptCount: action.attemptCount + 1,
      updatedAt: now,
      errorMessage: null,
    })));

    try {
      const settledAt = new Date().toISOString();
      let synced = 0;
      let conflicts = 0;
      let failed = 0;

      for (const action of actions) {
        try {
          if (this.isFieldEvidencePhotoUpload(action)) {
            const serverId = await this.uploadFieldEvidencePhoto(action);
            synced += 1;
            await fieldDb.fieldOutbox.update(action.id!, {
              status: 'synced',
              entityId: serverId ?? action.entityId ?? null,
              errorMessage: null,
              syncedAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
            continue;
          }

          const response: any = await apiClient.post('/sync/push', {
            actions: [this.toPushPayload(action)],
          });
          const body = resolveSyncPushResponse(response);
          const result = (body.results ?? []).find((row) => row.clientUuid === action.clientUuid);
          const actionSettledAt = new Date().toISOString();

          if (!result) {
            failed += 1;
            await fieldDb.fieldOutbox.update(action.id!, {
              status: 'failed',
              errorMessage: 'Sync response did not include this action',
              updatedAt: actionSettledAt,
            });
            continue;
          }

          if (result.status === 'applied' || result.status === 'duplicate') {
            synced += 1;
            await fieldDb.fieldOutbox.update(action.id!, {
              status: 'synced',
              entityId: result.serverId ?? action.entityId ?? null,
              errorMessage: null,
              syncedAt: actionSettledAt,
              updatedAt: actionSettledAt,
            });
            continue;
          }

          if (result.status === 'conflict') {
            conflicts += 1;
            await fieldDb.fieldOutbox.update(action.id!, {
              status: 'conflict',
              entityId: result.serverId ?? action.entityId ?? null,
              errorMessage: result.message ?? 'Conflict requires review',
              updatedAt: actionSettledAt,
            });
            continue;
          }

          failed += 1;
          await fieldDb.fieldOutbox.update(action.id!, {
            status: 'failed',
            errorMessage: result.message ?? 'Sync action was rejected',
            updatedAt: actionSettledAt,
          });
        } catch (error) {
          failed += 1;
          await fieldDb.fieldOutbox.update(action.id!, {
            status: 'failed',
            errorMessage: error instanceof Error ? error.message : 'Sync push failed',
            updatedAt: new Date().toISOString(),
          });
        }
      }

      await fieldDb.transaction('rw', fieldDb.syncMetadata, async () => {
        await fieldDb.syncMetadata.put({
          key: 'lastPushedAt',
          value: settledAt,
          updatedAt: settledAt,
        });
      });

      return { synced, conflicts, failed };
    } catch (error) {
      const failedAt = new Date().toISOString();
      await fieldDb.fieldOutbox.bulkPut(actions.map((action) => ({
        ...action,
        status: 'failed',
        attemptCount: action.attemptCount + 1,
        errorMessage: error instanceof Error ? error.message : 'Sync push failed',
        updatedAt: failedAt,
      })));
      return { synced: 0, conflicts: 0, failed: actions.length };
    }
  }

  async pull(): Promise<{ cursor: string; recordsCached: number }> {
    await this.ensureFieldCacheSchemaVersion();
    const since = await fieldDb.syncMetadata.get('pullCursor');
    const response: any = await apiClient.get('/sync/pull', {
      params: since?.value ? { since: since.value } : {},
    });
    const body = resolveSyncPullResponse(response);
    const cachedAt = new Date().toISOString();
    let recordsCached = 0;

    await fieldDb.transaction('rw', fieldDb.fieldCache, fieldDb.syncMetadata, async () => {
      for (const [collection, records] of Object.entries(body.data ?? {})) {
        const entityType = entityMap[collection];
        if (!entityType || !Array.isArray(records)) continue;

        await fieldDb.fieldCache.bulkPut(records.map((record) => {
          recordsCached += 1;
          return {
            key: `${entityType}:${record.id}`,
            entityType,
            entityId: String(record.id),
            projectId: record.projectId ?? null,
            payload: record,
            serverUpdatedAt: record.updatedAt ?? record.createdAt ?? null,
            cachedAt,
          };
        }));
      }

      await fieldDb.syncMetadata.put({
        key: 'pullCursor',
        value: body.cursor,
        updatedAt: cachedAt,
      });
      await fieldDb.syncMetadata.put({
        key: 'lastPulledAt',
        value: body.serverTime,
        updatedAt: cachedAt,
      });
    });

    return { cursor: body.cursor, recordsCached };
  }

  async syncNow(): Promise<{ cursor: string; recordsCached: number; pushed: { synced: number; conflicts: number; failed: number } }> {
    await this.ensureFieldCacheSchemaVersion();
    const pushed = await this.flushPending();
    const pulled = await this.pull();
    return { ...pulled, pushed };
  }

  private toPushPayload(action: FieldOutboxAction) {
    return {
      clientUuid: action.clientUuid,
      entityType: action.entityType,
      actionType: action.actionType,
      entityId: action.entityId ?? null,
      payload: action.payload,
      baseVersionAt: action.baseVersionAt ?? null,
    };
  }

  private isFieldEvidencePhotoUpload(action: FieldOutboxAction): boolean {
    const payload = action.payload as Record<string, any> | null;
    const metadata = payload?.metadata as Record<string, any> | undefined;
    return action.entityType === 'field_evidence' &&
      action.actionType === 'create' &&
      typeof metadata?.dataUrl === 'string' &&
      metadata.dataUrl.length > 0;
  }

  private async uploadFieldEvidencePhoto(action: FieldOutboxAction): Promise<string | null> {
    const payload = action.payload as Record<string, any>;
    const metadata = payload.metadata as Record<string, any>;
    const projectId = String(payload.projectId ?? action.projectId ?? '');
    const taskId = String(payload.taskId ?? '');

    if (!projectId || !taskId) {
      throw new Error('Photo evidence is missing project or task context');
    }

    const blob = dataUrlToBlob(String(metadata.dataUrl));
    const response: any = await apiClient.post('/documents/field-evidence/photo', blob, {
      params: { projectId, taskId },
      headers: {
        'Content-Type': blob.type || String(metadata.mimeType ?? 'image/jpeg'),
        'X-Client-Uuid': action.clientUuid,
        'X-File-Name': encodeURIComponent(String(metadata.fileName ?? 'field-photo.jpg')),
        'X-Captured-At': String(payload.capturedAt ?? payload.createdAt ?? ''),
        'X-Notes': encodeURIComponent(String(payload.notes ?? 'Field Mode task photo evidence')),
        ...(payload.wbsId ? { 'X-Wbs-Id': String(payload.wbsId) } : {}),
        ...(payload.costCodeId ? { 'X-Cost-Code-Id': String(payload.costCodeId) } : {}),
      },
    });
    const body = resolveFieldEvidencePhotoUploadResponse(response);
    return body.fieldEvidenceId ?? null;
  }
}

export default new FieldSyncService();
